import { z } from 'zod';
import { continuityReferenceSchema } from './video.js';
/**
 * Fase 10 — Creador virtual. Identidad ficticia adulta, versionada; brief del
 * proyecto; precios oficiales de imagen para estimar las referencias.
 */
export const IDENTITY_LIMITS = {
    nameMaxLength: 120,
    descriptionMaxLength: 4_000,
    lockedTraitsMax: 20,
    referencesMax: 8,
    /** Veo admite 3 referencias por petición: las de identidad van primero. */
    referencesSentToVideo: 3,
    rightsMaxLength: 500,
    productMessagesMax: 10,
};
export const identityRevisionStatusSchema = z.enum(['DRAFT', 'APPROVED']);
export const referenceSourceSchema = z.enum(['UPLOAD', 'GENERATED']);
export const referenceAngleSchema = z.enum(['front', 'three_quarter', 'profile', 'full_body', 'other']);
export const createIdentityRequestSchema = z.object({
    name: z.string().trim().min(1).max(IDENTITY_LIMITS.nameMaxLength),
    description: z.string().trim().min(1).max(IDENTITY_LIMITS.descriptionMaxLength),
    /** Rasgos invariantes, en inglés: los usan los prompts. */
    lockedTraits: z.array(z.string().trim().min(1).max(500)).min(1).max(IDENTITY_LIMITS.lockedTraitsMax),
});
export const updateRevisionRequestSchema = z.object({
    description: z.string().trim().min(1).max(IDENTITY_LIMITS.descriptionMaxLength).optional(),
    lockedTraits: z.array(z.string().trim().min(1).max(500)).min(1).max(IDENTITY_LIMITS.lockedTraitsMax).optional(),
});
export const addReferenceRequestSchema = z.object({
    assetId: z.uuid(),
    angle: referenceAngleSchema.default('other'),
    /** Obligatoria: de dónde sale la imagen y con qué derecho se usa. */
    rightsDeclaration: z.string().trim().min(1).max(IDENTITY_LIMITS.rightsMaxLength),
});
export const generateReferencesRequestSchema = z.object({
    /** Ángulos a generar en esta tanda. */
    angles: z.array(referenceAngleSchema).min(1).max(4),
    quality: z.enum(['low', 'medium', 'high']).default('medium'),
});
export const identityReferenceSchema = z.object({
    id: z.uuid(),
    assetId: z.uuid(),
    contentUrl: z.string(),
    role: z.string(),
    angle: z.string().nullable(),
    rightsDeclaration: z.string(),
    source: referenceSourceSchema,
});
export const identityRevisionSchema = z.object({
    id: z.uuid(),
    number: z.number().int(),
    description: z.string(),
    lockedTraits: z.array(z.string()),
    status: identityRevisionStatusSchema,
    approvedAt: z.string().nullable(),
    /** true si algún proyecto la usa: ya no se edita. */
    inUse: z.boolean(),
    references: z.array(identityReferenceSchema),
    createdAt: z.string(),
});
export const identitySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    referenceProjectId: z.uuid().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    revisions: z.array(identityRevisionSchema),
});
export const identitySummarySchema = identitySchema.omit({ revisions: true }).extend({
    latestRevision: z.number().int(),
    approvedRevision: z.number().int().nullable(),
});
// --- Brief del creador virtual --------------------------------------------------
export const creatorGoalSchema = z.enum(['EDUCATIONAL', 'LIFESTYLE', 'PRODUCT']);
export const creatorBriefInputSchema = z.object({
    expectedRevision: z.number().int().min(1),
    goal: creatorGoalSchema,
    locationDescription: z.string().trim().min(1).max(2_000),
    locationAssetIds: z.array(z.uuid()).max(3).default([]),
    productName: z.string().trim().max(200).nullable().default(null),
    productDescription: z.string().trim().max(2_000).nullable().default(null),
    productAssetIds: z.array(z.uuid()).max(3).default([]),
    /** Solo mensajes aportados por el usuario; el sistema no inventa beneficios. */
    productMessages: z.array(z.string().trim().min(1).max(300)).max(IDENTITY_LIMITS.productMessagesMax).default([]),
});
export const creatorBriefSchema = creatorBriefInputSchema.omit({ expectedRevision: true }).extend({
    updatedAt: z.string(),
});
export const linkIdentityRequestSchema = z.object({
    expectedRevision: z.number().int().min(1),
    identityRevisionId: z.uuid(),
});
export const projectIdentitySchema = z.object({
    identityId: z.uuid(),
    identityName: z.string(),
    revisionId: z.uuid(),
    revisionNumber: z.number().int(),
    lockedTraits: z.array(z.string()),
    references: z.array(continuityReferenceSchema.extend({ contentUrl: z.string() })),
    linkedAt: z.string(),
});
// --- Precios de imagen (OpenAI, USD por imagen) -----------------------------------
/**
 * Tarifa publicada por imagen según calidad y tamaño. Fuente:
 * developers.openai.com/api/docs/guides/image-generation, leída el 2026-10-07.
 * Sirve para estimar y reservar; la API no devuelve el importe.
 */
export const IMAGE_PRICE_USD = {
    'gpt-image-2': {
        low: { '1024x1024': '0.006', '1024x1536': '0.005', '1536x1024': '0.005' },
        medium: { '1024x1024': '0.053', '1024x1536': '0.041', '1536x1024': '0.041' },
        high: { '1024x1024': '0.211', '1024x1536': '0.165', '1536x1024': '0.165' },
    },
    'gpt-image-2.5-sunburst': {
        low: { '1024x1024': '0.006', '1024x1536': '0.005', '1536x1024': '0.005' },
        medium: { '1024x1024': '0.053', '1024x1536': '0.041', '1536x1024': '0.041' },
        high: { '1024x1024': '0.211', '1024x1536': '0.165', '1536x1024': '0.165' },
    },
    'gpt-image-1.5': {
        low: { '1024x1024': '0.009', '1024x1536': '0.013', '1536x1024': '0.013' },
        medium: { '1024x1024': '0.034', '1024x1536': '0.050', '1536x1024': '0.050' },
        high: { '1024x1024': '0.133', '1024x1536': '0.200', '1536x1024': '0.200' },
    },
    'gpt-image-1': {
        low: { '1024x1024': '0.011', '1024x1536': '0.016', '1536x1024': '0.016' },
        medium: { '1024x1024': '0.042', '1024x1536': '0.063', '1536x1024': '0.063' },
        high: { '1024x1024': '0.167', '1024x1536': '0.250', '1536x1024': '0.250' },
    },
    'gpt-image-1-mini': {
        low: { '1024x1024': '0.005', '1024x1536': '0.006', '1536x1024': '0.006' },
        medium: { '1024x1024': '0.011', '1024x1536': '0.015', '1536x1024': '0.015' },
        high: { '1024x1024': '0.036', '1024x1536': '0.052', '1536x1024': '0.052' },
    },
};
export const IMAGE_PRICE_SOURCE = {
    url: 'https://developers.openai.com/api/docs/guides/image-generation',
    checkedAt: '2026-10-07',
    currency: 'USD',
};
export function estimateImageCostUsd(model, quality, size) {
    const precio = IMAGE_PRICE_USD[model]?.[quality]?.[size];
    return precio === undefined ? null : Number(precio).toFixed(4);
}
//# sourceMappingURL=identity.js.map